import React, { forwardRef, useEffect, useState } from "react";
import { Task, TaskStatus, workflowEventDictionary } from "../../types/tasks";
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { useGlobalTheme } from "../../context/GlobalThemeContext";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteIcon from "@mui/icons-material/Delete";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getRelativeTime } from "../../utils/dateutils";
import { getLocalizedTitle } from "../../utils/taskutils";
import i18n from "../../i18n";

interface Props {
  task: Task;
  status: TaskStatus;
  showDeleteIcon: boolean;
  onDelete: () => void;
  onClick?: (task: Task) => void;
  index?: number;
}

const statusBorderColors: Record<TaskStatus, string> = {
  todo: "#A5B4FC",
  doing: "#93C5FD",
  feedback: "#F87171",
  done: "#93F090",
};

const cardVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.03,
      type: "spring" as const,
      stiffness: 120,
      damping: 20,
      opacity: { duration: 0.3 },
    },
  }),
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
};

const MotionDiv = motion(
  forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, ref) => (
    <div ref={ref} {...props} />
  )),
);

const TaskCard = forwardRef<HTMLDivElement, Props>(
  ({ task, status, showDeleteIcon, onDelete, onClick, index }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { t } = useTranslation();
    const isDark = useGlobalTheme();

    const takenOver = task.takenOverByHuman === true;
    const reviewDone = task.reviewed === true;
    const helpDone = task.readyToAdvance === true;

    const showReviewedChip = !takenOver && reviewDone && status === "done";
    const showResolvedChip = !takenOver && helpDone && status === "done";

    const [, forceUpdate] = useState(0);
    useEffect(() => {
      const interval = setInterval(() => {
        forceUpdate((val) => val + 1);
      }, 60000);
      return () => clearInterval(interval);
    }, []);

    const truncate = (str: string, limit: number) => {
      if (str.length > limit) {
        return str.substring(0, limit) + "...";
      }
      return str;
    };

    const truncatedTitle = truncate(
      getLocalizedTitle(task.title, i18n.language),
      75,
    );

    return (
      <MotionDiv
        layout
        layoutId={task.id}
        custom={index}
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={cardVariants}
        onClick={() => onClick?.(task)}
        style={{
          cursor: onClick ? "pointer" : "default",
        }}
        ref={ref}
      >
        <Paper
          elevation={3}
          sx={{
            minHeight: isMobile ? "8em" : "10em",
            maxHeight: isMobile ? "13em" : "15em",
            justifyContent: "center",
            borderRadius: "0.5em",
            px: isMobile ? "0.625em" : "1em",
            py: isMobile ? "0.625em" : "1em",
            display: "flex",
            flexDirection: "column",
            gap: "0.75em",
            backgroundColor: isDark ? "rgba(30,30,30,0.95)" : "#fff",
            boxShadow: "0 0.25em 0.625em rgba(0, 0, 0, 0.1)",
            borderLeft: `0.375em solid ${statusBorderColors[status]}`,
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-0.125em)",
              boxShadow: "0 0.375em 0.875em rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body1"
              fontWeight={600}
              color="text.primary"
              sx={{ lineHeight: 1.2, fontSize: isMobile ? "0.9em" : "0.95em" }}
            >
              {truncatedTitle}
            </Typography>

            {/* chips */}
            {status === "feedback" && !takenOver && (
              <Tooltip title={t("taskCard.tooltipFeedback")}>
                <Box
                  sx={{
                    width: "1.75em",
                    height: "1.75em",
                    minWidth: "1.75em",
                    minHeight: "1.75em",
                    borderRadius: "50%",
                    backgroundColor: "#F87171",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 0 0.25em rgba(0,0,0,0.15)",
                  }}
                >
                  <ReportProblemOutlinedIcon
                    sx={{ color: "#fff", fontSize: "1em" }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>

          {task.eventType && (
            <Box
              sx={{
                mt: "0.125em",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.125em",
                backgroundColor: "#DBEAFE",
                color: "#1E40AF",
                px: "1em",
                py: "0.5em",
                borderRadius: "0.375em",
                fontWeight: 600,
                fontSize: "0.7em",
                width: "fit-content",
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: "1em" }} />
              {t(`workflowEvent.${workflowEventDictionary[task.eventType]}`)}
            </Box>
          )}

          {takenOver && (
            <Box
              sx={{
                mt: "0.25em",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25em",
                backgroundColor: "#E1BEE7",
                color: "#6A1B9A",
                px: "1em",
                py: "0.5em",
                borderRadius: "0.375em",
                fontWeight: 600,
                fontSize: "0.7em",
                width: "fit-content",
              }}
            >
              {t("taskCard.takenOverByUser")}
            </Box>
          )}

          {showReviewedChip && (
            <Box
              sx={{
                mt: "0.25em",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25em",
                backgroundColor: "#FEF3C7",
                color: "#92400E",
                px: "1em",
                py: "0.5em",
                borderRadius: "0.375em",
                fontWeight: 600,
                fontSize: "0.7em",
                width: "fit-content",
              }}
            >
              <ChatBubbleOutlineIcon fontSize="small" />
              {t("taskCard.reviewed")}
            </Box>
          )}

          {showResolvedChip && (
            <Box
              sx={{
                mt: "0.25em",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25em",
                backgroundColor: "#FFEDD5",
                color: "#C2410C",
                px: "1em",
                py: "0.5em",
                borderRadius: "0.375em",
                fontWeight: 600,
                fontSize: "0.7em",
                width: "fit-content",
              }}
            >
              <CheckCircleOutlineIcon fontSize="small" />
              {t("taskCard.resolved")}
            </Box>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap="0.5em">
              <AccessTimeIcon sx={{ fontSize: "0.875em", color: "#64748B" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7em" }}
              >
                {getRelativeTime(task.createdAt ?? new Date(), t)}
              </Typography>
            </Box>

            {showDeleteIcon && (
              <Tooltip title={t("taskCard.deleteTooltip")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{ p: "0.125em" }}
                >
                  <DeleteIcon sx={{ fontSize: "1.125em", color: "#F87171" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </MotionDiv>
    );
  },
);

export default TaskCard;
